<template>
  <SidebarSettingsGroup
    title="Style & Animation"
    collapsible
    :element-type="element.type"
    data-test="graphics-groupSettings-styleAndAnimation"
  >
    <opacity-settings v-model="opacity"/>

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="checkbox header-checkbox"
        >Background color
        <input
          v-model="background"
          type="checkbox"
          data-test="graphics-groupSettings-toggleBackground"
          data-entity="graphics-groupSettings-toggleBackground"
        />
        <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check"></span>
        </div>
      </label>

      <div v-if="background" class="settings-row settings-row-xs">
        <label class="radio inline-radio" for="solid"
          >Solid
          <input
            id="solid"
            v-model="fillStyle"
            type="radio"
            value="solid"
            data-test="graphics-groupSettings-fillStyleSolid"
            data-entity="graphics-groupSettings-fillStyleSolid"
          />
          <div class="checkmark"></div>
        </label>
        <label class="radio inline-radio" for="gradient"
          >Gradient
          <input
            id="gradient"
            v-model="fillStyle"
            type="radio"
            value="gradient"
            data-test="graphics-groupSettings-fillStyleGradient"
          />
          <div class="checkmark"></div>
        </label>

        <div
          v-if="fillStyle == 'solid'"
          class="colorpicker"
          data-test="graphics-groupSettings-fillStyleSolidColor"
        >
          <colorpicker v-model="backgroundColor" />
        </div>

        <div
          v-else
          class="settings-row settings-row-xs"
          data-test="graphics-groupSettings-fillStyleGradient"
        >
          <div class="settings-col-6 settings-col-xs">
            <div class="input-group">
              <validation-field as="input"
                v-model.number="gradientDirection"
                rules="required|numeric"
                type="number"
                name="gradientDirection"
                :class="{ 'is-invalid': validationErrors['gradientDirection'] }"
                data-test="graphics-groupSettings-setGradientDirection"
                data-entity="graphics-groupSettings-setGradientDirection"
              />
              <span class="input-group-addon symbol">°</span>
            </div>
          </div>
          <div class="settings-col-3 settings-col-xs colorpicker">
            <colorpicker v-model="gradientColorOne" />
          </div>
          <div class="settings-col-3 settings-col-xs colorpicker">
            <colorpicker v-model="gradientColorTwo" />
          </div>
        </div>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <background-image
      :element="element"
      :scroll-offset="scrollOffset"
      @modal="(opts) => $emit('modal', opts)"
    />

    <hr class="sidebar-divider" />

    <box-shadow-settings :element="element"></box-shadow-settings>

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="checkbox header-checkbox"
        >Blur
        <input type="checkbox" v-model="blur" data-test="group-input-blur" />
        <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check"></span>
        </div>
      </label>

      <div v-if="blur" class="input-group">
        <validation-field as="input"
          v-model.number="blurSize"
          rules="required|numeric|min:0"
          type="number"
          name="blurSize"
          min="0"
          :class="{ 'is-invalid': validationErrors['blurSize'] }"
          data-test="graphics-groupSettings-setBlurSize"
        />
        <span class="input-group-addon">px</span>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <animation-settings :element="element"></animation-settings>
  </SidebarSettingsGroup>
</template>

<script>
import SettingsMixins from '../../mixins/settings-mixins';

import AnimationSettings from './Animation.vue';
import OpacitySettings from './Opacity.vue';
import BoxShadowSettings from './BoxShadow.vue';
import BackgroundImage from './BackgroundImage.vue';

export default {
  emits: ['modal'],
  name: 'GroupSettingsComponent',
  mixins: [SettingsMixins],
  props: ['element', 'scrollOffset'],
  components: {
    'animation-settings': AnimationSettings,
    'opacity-settings': OpacitySettings,
    'box-shadow-settings': BoxShadowSettings,
    BackgroundImage,
  },
  computed: {
    opacity: {
      get() {
        return this.getPropertyValue('styles.opacity', { emptyValue: 1 });
      },
      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },
    background: {
      get() {
        return this.getPropertyValue('background');
      },
      set(v) {
        this.updateOneElementValue('background', v);
      },
    },
    fillStyle: {
      get() {
        return this.getPropertyValue('fillStyle');
      },
      set(v) {
        this.updateOneElementValue('fillStyle', v);
      },
    },
    backgroundColor: {
      get() {
        return this.getPropertyValue('backgroundColor');
      },
      set(v) {
        this.updateOneElementValue('backgroundColor', v);
      },
    },
    gradientDirection: {
      get() {
        return this.getPropertyValue('gradientDirection');
      },
      set(v) {
        this.updateOneElementValue('gradientDirection', v);
      },
    },
    gradientColorOne: {
      get() {
        return this.getPropertyValue('gradientColorOne');
      },
      set(v) {
        this.updateOneElementValue('gradientColorOne', v);
      },
    },
    gradientColorTwo: {
      get() {
        return this.getPropertyValue('gradientColorTwo');
      },
      set(v) {
        this.updateOneElementValue('gradientColorTwo', v);
      },
    },
    backgroundImage: {
      get() {
        return this.getPropertyValue('backgroundImage');
      },
      set(v) {
        this.updateOneElementValue('backgroundImage', v);
      },
    },
    backgroundSize: {
      get() {
        return this.getPropertyValue('backgroundSize');
      },
      set(v) {
        this.updateOneElementValue('backgroundSize', v);
      },
    },
    blur: {
      get() {
        return this.getPropertyValue('blur');
      },
      set(v) {
        this.updateOneElementValue('blur', v);
      },
    },
    blurSize: {
      get() {
        return this.getPropertyValue('blurSize');
      },
      set(v) {
        this.updateOneElementValue('blurSize', v);
      },
    },
  },
  methods: {
    removeBackgroundImage() {
      this.updateOneElementValue('backgroundImageUrl', '');
      this.updateOneElementValue('backgroundImageMeta', {});
    },
    selectAsset(type, setting) {
      this.$emit('modal', { open: true, type });

      window.assetmanfn = ({ asset, meta: additiveMeta = {} }) => {
        this.updateOneElementValue(setting, asset?.media_url || '');

        if (setting === 'imageUrl') {
          this.updateOneElementValue('imageMeta', additiveMeta);
        }

        if (setting === 'backgroundImageUrl') {
          this.updateOneElementValue('backgroundImageMeta', additiveMeta);
        }

        this.$emit('modal', { open: false });
      };
    },
  },
};
</script>
